// app/src/features/timeline/renderers/renderTimelineContent.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import ForecastLane from "../components/ForecastLane.jsx";
import { CitationCard, Tooltip } from "../../../components/Overlays.jsx";
import ProbabilityPlot from "../../../components/timeline/ProbabilityPlot.jsx";
import { ResolvedOutcomeMark } from "../../../components/timeline/ResolvedOutcomeMark.jsx";
import TimelineBackdrop from "../../../components/timeline/TimelineBackdrop.jsx";
import { isLightTheme } from "../../../lib/colors.js";
import { STATUS } from "../../../lib/forecast/model.js";
import { extractMultiway } from "../../../lib/forecast/multiway.js";
import { STREAM_HALF } from "./constants.js";
import AnswerCheckPanel from "../../resolution/components/AnswerCheckPanel.jsx";
import AnswerCheckTrack from "../../resolution/components/AnswerCheckTrack.jsx";
import { ANSWER_BOX_GAP, ANSWER_BOX_MIN_W, ANSWER_BOX_W } from "../../resolution/answerBox.js";
import { resolutionRightBufferFor } from "../../../lib/timeline/multiwayMarker.js";
import { R_START } from "../../../lib/timeline/geometry.js";

// The timeline's scrolling content.
export function renderTimelineContent(ctx) {
  const {
    LANE_COLORS,
    RES_SZ,
    SPINE_Y,
    _awaitCapMs,
    _resCapMs,
    _resVerdictStatus,
    active,
    answerBox,
    allMultiwayEnabled,
    asyncMoments,
    axisHoverX,
    branchLayout,
    citCard,
    compareOn,
    debugForceGenerating,
    displayActive,
    effectiveToday,
    fadeEndX,
    fadeStartX,
    forecastPointerCount,
    forecastTracking,
    forecastingPhase,
    genX,
    hov,
    hovEvtRef,
    hovPos,
    hoveredBin,
    laneCtx,
    laneGeom,
    laneRuns,
    outcomeHistoryLinesEnabled,
    ppd,
    questionFade,
    railLineFade,
    resScreenX,
    resolvedMark,
    runStatusOn,
    setHov,
    setHovPos,
    showGenLine,
    showTodayLine,
    snapTruncateMs,
    theme,
    themeRaw,
    tickOpacity,
    timelineToday,
    todayX,
    uiZoom,
    viewLeft,
    viewSnap,
    visTicks,
    vw,
  } = ctx;
  return (
    <div
      data-testid="forecast-timeline-content"
      style={{
        position: "relative",
        minHeight: "100%",
        // The stack's bottom already includes its last row's extent.
        height: Math.max(0, branchLayout.totalHeight + 6),
      }}
    >
      <TimelineBackdrop
        todayX={todayX}
        vw={vw}
        fadeStartX={fadeStartX}
        fadeEndX={fadeEndX}
        theme={theme}
      />
      {visTicks.map((t, i) => {
        const sx = (t.x / ppd - viewLeft) * ppd;
        const op = tickOpacity(sx);
        if (op <= 0) return null;
        // Light-theme grid greys are hand-tuned so major lines read clearly and minor lines
        // stay subtle but visible on the near-white panel.
        const gridMajor = isLightTheme(theme) ? "#b0b0b0a8" : `${themeRaw.border}99`;
        const gridMinor = isLightTheme(theme) ? "#d0d0d055" : `${themeRaw.border}2a`;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: sx,
              top: 0,
              width: 1,
              height: "100%",
              background: t.major ? gridMajor : gridMinor,
              opacity: op,
              pointerEvents: "none",
              ...railLineFade,
            }}
          />
        );
      })}

      {/* ALL LANES: a single forecast (1 lane) and N-way compare render through ONE
         <ForecastLane> map, and each lane is selection-agnostic (compareOn / color /
         awaiting are config, never store reads). The lanes are RAW runs in lane order
         (primary first): compare mode uses selectComparisonLanes(runState), single mode
         falls back to the active run (selectActiveRun). */}
      {/* The fade wrapper is a position:relative element (NOT an absolute/inset
         overlay): opacity/animation fades the whole subtree including the
         absolutely-positioned lanes. Normally it is 0-height, covers no area and sets
         no pointerEvents (an inherited none would kill the lanes' own hit areas, e.g.
         the resolution-pie click). With the question fade on it must span the content
         for its mask to cover the lanes, so it takes pointerEvents:none itself and
         `.qfade-layer > *` (GlobalStyles) restores them on its children. zIndex:6 keeps
         the lanes above the today/forecast lines during the fade's stacking context.
         Lanes position from content's (0,0) either way, where the wrapper starts. */}
      <div
        // Key the fade on the DISPLAYED content, not the SELECTION. The
        // lanes render from `viewSnap` (displayActive/laneRuns), and while
        // an async vintage loads `viewSnap` is the HELD previous snapshot
        // (flash-fix) even though `viewMomentIso` has already advanced.
        // Keying on `viewMomentIso` remounted + faded the OLD content in at
        // selection time, then swapped in the resolved content with no
        // animation. `viewSnap.generatedAt` equals the selected moment only
        // once the new snapshot is in hand, so the fade now fires exactly
        // when the new forecast lands (and holds — no remount — during the
        // load). Live (viewSnap null) keys to "live".
        key={`lanes-${viewSnap?.generatedAt ?? "live"}`}
        className={questionFade ? "qfade-layer" : undefined}
        style={{
          position: "relative",
          zIndex: 6,
          animation: "simpleFadeIn 0.35s ease-out",
          ...(questionFade ? { height: "100%", pointerEvents: "none", ...questionFade } : {}),
        }}
      >
        {laneRuns.map(({ root, branches: laneBranches, scenarios }, i) => {
          const lane = laneGeom.lanes[i];
          if (!lane) return null;
          const laneColor = i === 0 ? themeRaw.purple : LANE_COLORS[i % LANE_COLORS.length];
          // Lane 0 (single + compare primary) follows the global forecastingPhase; a
          // secondary lane carries no global phase, so derive it from its own run
          // (in-flight marker OR no settled result yet). Kept inline (not in the laneRuns
          // memo) because it reads forecastingPhase, which is NOT part of the [runState,
          // compareOn] key.
          const laneAwaiting =
            i === 0
              ? forecastingPhase === "awaiting" || debugForceGenerating
              : root.status === STATUS.RUNNING ||
                root.status === STATUS.PREPROCESSING ||
                // A completed multi-value ("multiway") run legitimately has null
                // probability + no events — its answer is the PMF (forecast_value),
                // NOT a P(YES). Exclude it so a compare-lane multi result doesn't
                // spin in a perpetual "Generating" pulse.
                (root.contract?.probability == null &&
                  (root.events || []).length === 0 &&
                  !extractMultiway(root.contract));
          return (
            <ForecastLane
              key={root.runId}
              root={root}
              branches={laneBranches}
              scenarios={scenarios}
              config={{
                laneIndex: i,
                isPrimary: i === 0,
                color: laneColor,
                palette: LANE_COLORS,
                showEngine: compareOn,
                compareOn,
                awaiting: laneAwaiting,
                // The run status card covers the lane on a first run.
                hideAwaiting: i === 0 && runStatusOn,
              }}
              layout={{ spineY: lane.spineY, branchYs: lane.branchYs }}
              ctx={laneCtx}
            />
          );
        })}
      </div>

      {resolvedMark && (
        <ResolvedOutcomeMark
          x={resolvedMark.x}
          vw={vw}
          theme={theme}
          spineY={SPINE_Y}
          streamHalf={STREAM_HALF}
          capMs={resolvedMark.ms}
          trace={{
            active,
            displayContract: displayActive?.contract,
            asyncMoments,
            hoveredBinLabel: hoveredBin,
            status: _resVerdictStatus,
            verdict: active?.resolution,
            allMultiway: allMultiwayEnabled,
            perOutcome: outcomeHistoryLinesEnabled,
            forecastTracking,
            pointerCount: forecastPointerCount,
          }}
        />
      )}
      {!resolvedMark && resScreenX !== null && resScreenX > -40 && resScreenX < vw + 40 && (
        <div
          key={`fline-main-${active?.id || ""}`}
          data-testid="forecast-line-main"
          style={{
            // A thin dashed accent line for the resolution date.
            position: "absolute",
            left: resScreenX,
            top: 0,
            width: 0,
            height: "100%",
            borderLeft: `1px dashed ${themeRaw.accent}`,
            opacity: 0.7,
            zIndex: 4,
            pointerEvents: "none",
            ...railLineFade,
            transformOrigin: "center",
            animation:
              forecastingPhase === "awaiting"
                ? "vertGrowFromCenter 0.5s ease-out 0.5s both"
                : "none",
          }}
        />
      )}

      {answerBox &&
        _awaitCapMs != null &&
        resScreenX != null &&
        (() => {
          // Right of the outcomes: past the bars' labels for a distribution, past the pie and
          // its "No answer yet" readout for a yes/no forecast.
          const dist = extractMultiway(displayActive?.contract);
          const markerRight =
            dist || allMultiwayEnabled
              ? resolutionRightBufferFor(dist?.buckets ?? null)
              : RES_SZ / 2 + 140;
          const left = resScreenX + markerRight + ANSWER_BOX_GAP;
          // Hidden once the marker itself is scrolled off either side.
          if (resScreenX > vw || left + ANSWER_BOX_W < -40) return null;
          // A narrow timeline shrinks the box, then pulls it back on screen over the marker's
          // labels, so Check now stays reachable.
          const width = Math.min(ANSWER_BOX_W, Math.max(ANSWER_BOX_MIN_W, vw - left - 12));
          const x = Math.min(left, vw - width - 12);
          const stop = (e) => e.stopPropagation();
          return (
            // telemetry-gesture: keeps presses in the answer box off the timeline's pan and popovers; its controls carry their own ids
            <div
              data-answer-check-box=""
              // Clicks and drags in the box belong to it, not to the timeline's pan and popovers.
              onMouseDown={stop}
              onPointerDown={stop}
              onDoubleClick={stop}
              style={{
                position: "absolute",
                left: x,
                top: SPINE_Y,
                transform: "translateY(-50%)",
                width,
                // Centred on the spine, so it may be at most twice the spine's height: taller,
                // it would rise above the top of the timeline, which cannot scroll up to it.
                maxHeight: Math.max(120, 2 * SPINE_Y - 16),
                overflowY: "auto",
                zIndex: 18,
              }}
            >
              <AnswerCheckPanel
                // Per forecast: a refusal note or an open fold never carries to the next one.
                key={active?.id}
                state={answerBox.state}
                checkNow={answerBox.checkNow}
                onCheckNow={answerBox.onCheckNow}
                nowMs={answerBox.nowMs}
                id={active?.id}
                style={{ marginTop: 0, maxWidth: "none" }}
              />
            </div>
          );
        })()}

      {answerBox && _awaitCapMs != null && (
        <AnswerCheckTrack
          check={answerBox.state}
          closeMs={_awaitCapMs}
          todayMs={timelineToday.getTime()}
          xAtMs={(ms) => ((ms - R_START) / 86400000 - viewLeft) * ppd}
          vw={vw}
        />
      )}

      {/* Probability-over-time plot line. Multi-value contracts carry no scalar
         `probability` at all, so the gate also accepts a bucket distribution (with
         the per-outcome lines on); requiring the scalar alone would hide the plot on
         exactly the questions those lines are for. The series comes from
         buildReforecastMoments (value produced at each moment, held forward). There
         is deliberately NO today→resolution segment: the distribution bars that
         replace the pie sit on the spine, not at a probability height, so such a
         connector would connect to nothing. */}
      {forecastTracking &&
        (active?.history?.length > 0 || forecastPointerCount > 0) &&
        active?.contract?.resolution_date &&
        (active?.contract?.probability != null ||
          (outcomeHistoryLinesEnabled && extractMultiway(active?.contract))) && (
          <ProbabilityPlot
            ctx={{
              active,
              // Rewind the plot's "now" with the timeline so the trace stops at
              // the vintage's today line (not the live one) when scrubbed.
              effectiveToday: timelineToday,
              SPINE_Y,
              STREAM_HALF,
              viewLeft,
              ppd,
              vw,
              axisHoverX,
              hov,
              hovPos,
              setHov,
              setHovPos,
              truncateMs: snapTruncateMs,
              asyncMoments,
              theme,
              // Terminal verdict (or debug force-resolution): cap the
              // past trace at the resolution moment so it
              // stops at the marker instead of drawing a flat line to today
              // (to the RIGHT of the pie when the horizon is in the past).
              resolvedCapMs: _resCapMs ?? _awaitCapMs,
              // Which line to draw: the hovered outcome's own history, or the
              // leading one when nothing is hovered. The plot resolves the
              // distribution itself from the same inputs the marker uses, so the
              // two cannot disagree about what the buckets are.
              perOutcome: outcomeHistoryLinesEnabled,
              allMultiway: allMultiwayEnabled,
              // The contract as DISPLAYED — the rewound vintage's while scrubbing.
              // The marker is drawn from this, so the plot must pick its outcomes
              // from it too, or a rewound vintage's bins would not be selectable.
              displayContract: displayActive?.contract,
              hoveredBinLabel: hoveredBin,
              verdictStatus: _resVerdictStatus,
              verdict: active?.resolution,
            }}
          />
        )}

      {hov &&
        !hov.startsWith("res") &&
        !hov.startsWith("rbr") &&
        hov.includes("::") &&
        hovEvtRef.current && (
          <Tooltip
            ev={hovEvtRef.current}
            x={hovPos.x}
            y={hovPos.y}
            today={effectiveToday.getTime()}
            zoom={uiZoom}
          />
        )}
      {citCard && (
        <CitationCard
          label={citCard.label}
          quotes={citCard.quotes}
          url={citCard.url}
          x={citCard.x}
          y={citCard.y}
          zoom={uiZoom}
        />
      )}

      {active && showTodayLine && todayX > -10 && todayX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: todayX,
            top: 0,
            width: 1,
            height: "100%",
            background: themeRaw.today,
            opacity: 0.45,
            zIndex: 5,
            pointerEvents: "none",
            ...railLineFade,
          }}
        />
      )}
      {/* `transform:translateX(-50%)` centers the 2px-wide line ON `genX`, so the first
         probability-line tick (also centered on `xAtMs(createdAt) === genX` via its own
         translateX(-50%)) lands exactly on the GENERATED line, not 1px to its left.
         Visibility is gated by showGenLine. */}
      {showGenLine && genX > -10 && genX < vw + 10 && (
        <div
          style={{
            position: "absolute",
            left: genX,
            top: 0,
            width: 1,
            height: "100%",
            background: `${themeRaw.gray}55`,
            zIndex: 5,
            pointerEvents: "none",
            transform: "translateX(-50%)",
            ...railLineFade,
          }}
        />
      )}
    </div>
  );
}
